import { useState, useCallback } from 'react';
import { chatbotService } from '../services/apiService';

export const useChatbotHistory = (userId) => {
  const [messages, setMessages] = useState([]); 
  const [cursor, setCursor] = useState(null); 
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [isFirstLoadComplete, setIsFirstLoadComplete] = useState(false);

  const parseMessages = (dataBatch) => {
    const reversedBatch = [...dataBatch].reverse();
    return reversedBatch.flatMap(item => [
      { id: `user-${item.message_id}`, text: item.question, isBot: false },
      { id: `bot-${item.message_id}`, text: item.answer, isBot: true }
    ]);
  };

  const loadInitialHistory = useCallback(async () => {
    if (isFirstLoadComplete || loading) return;
    setLoading(true);
    try {
      const response = await chatbotService.getHistory(userId, null);
      const newData = response.data || [];
      
      if (newData.length > 0) {
        const formattedMessages = parseMessages(newData);
        setMessages(formattedMessages);
        
        const lastItem = newData[newData.length - 1];
        setCursor(lastItem.message_id);
      } else {
        setHasMore(false);
      }
      setIsFirstLoadComplete(true);
    } catch (error) {
      console.error("Lỗi khi fetch lịch sử ban đầu:", error);
    } finally {
      setLoading(false);
    }
  }, [userId, isFirstLoadComplete, loading]);

  const loadMoreHistory = useCallback(async () => {
    if (loadingMore || !hasMore || !cursor) return;
    
    setLoadingMore(true);
    try {
      const response = await chatbotService.getHistory(userId, cursor);
      const newData = response.data || [];
      
      if (newData.length > 0) {
        const formattedMessages = parseMessages(newData);
        setMessages((prevMessages) => [...formattedMessages, ...prevMessages]);
        
        const lastItem = newData[newData.length - 1];
        setCursor(lastItem.message_id);
      } else {
        setHasMore(false);
      }
    } catch (error) {
      console.error("Lỗi khi load thêm lịch sử:", error);
    } finally {
      setLoadingMore(false);
    }
  }, [userId, cursor, loadingMore, hasMore]);

  // Gửi câu hỏi và xử lý streaming trả về
  const sendMessageStream = async (question) => {
    // 1. Push user message vào UI
    const tempUserId = `user-temp-${Date.now()}`;
    const tempBotId = `bot-temp-${Date.now()}`;

    setMessages(prev => [
      ...prev, 
      { id: tempUserId, text: question, isBot: false }
    ]);

    // 2. Push message tạm thời của Bot với isStreaming định danh
    setMessages(prev => [
      ...prev,
      { id: tempBotId, text: '', isBot: true, isStreaming: true }
    ]);

    try {
      const response = await chatbotService.streamChat(userId, question);

      if (!response.ok) {
        throw new Error(`API error: ${response.status}`);
      }

      if (!response.body) {
        throw new Error("Môi trường không hỗ trợ ReadableStream.");
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder("utf-8");
      let buffer = ""; // Chuỗi đệm (buffer) để chống lỗi JSON bị cắt dở bởi chunk mạng

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        // Giải mã mảng bytes thành text và nối vào buffer
        // Các raw chunk từ ReadableStream (chưa decode)
        const chunkStr = decoder.decode(value, { stream: true });
        buffer += chunkStr;

        const lines = buffer.split('\n');

        // Giữ lại phần tử cuối cùng vì có thể nó bị cắt dở
        buffer = lines.pop();

        for (const line of lines) {
          const trimmedLine = line.trim();
          if (!trimmedLine) continue;

          // API sử dụng định dạng Server-Sent Events (SSE): "event: ..." và "data: {...}"
          // Ta thu thập nội dung đằng sau prefix "data:" để parse JSON
          if (trimmedLine.startsWith('data:')) {
            const jsonStr = trimmedLine.replace(/^data:\s*/, '');
            if (!jsonStr) continue;

            try {
              const parsed = JSON.parse(jsonStr);

              if (parsed.type === 'chunk') {
                setMessages(prev => 
                  prev.map(msg => 
                    msg.id === tempBotId 
                      ? { ...msg, text: msg.text + (parsed.content || "") }
                      : msg
                  )
                );
              } else if (parsed.type === 'done') {
                const finalData = parsed.data;

                setMessages(prev => 
                  prev.map(msg => {
                    if (msg.id === tempBotId) {
                      return {
                        ...msg,
                        id: `bot-${finalData.message_id}`,
                        text: finalData.answer,
                        isStreaming: false,
                        created_at: finalData.created_at
                      };
                    }
                    if (msg.id === tempUserId) {
                      return {
                        ...msg,
                        id: `user-${finalData.message_id}`,
                        created_at: finalData.created_at
                      };
                    }
                    return msg;
                  })
                );
              }
            } catch (err) {
              console.error("Lỗi khi parse JSON dòng:", jsonStr, err);
            }
          }
        }

      }

    } catch (error) {
      console.error("Lỗi khi stream tin nhắn:", error);
      // Xử lý báo lỗi trên UI bot
      setMessages(prev => 
        prev.map(msg => 
          msg.id === tempBotId 
            ? { ...msg, text: "Xin lỗi, đã xảy ra lỗi kết nối.", isStreaming: false, isError: true }
            : msg
        )
      );
    }
  };

  return { 
    messages, 
    loadInitialHistory, 
    loadMoreHistory, 
    loading, 
    loadingMore, 
    hasMore,
    isFirstLoadComplete,
    sendMessageStream
  };
};
